import { useMemo, type ReactNode } from 'react';
import { CheckCircleOutlined, DownOutlined, RightOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import type { MismatchDetail, DifferenceItem } from '../../../infrastructure/models';
import type { EnvironmentPaneProps } from './comparisonResultTypes';
import { getDiffTypeLabel } from './comparisonResultTypes';
import styles from './ComparisonResult.module.scss';

const computeCharDiff = (a: string, b: string): { parts: { text: string; highlighted: boolean }[] }[] => {
  if (a === b) return [{ parts: [{ text: a, highlighted: false }] }, { parts: [{ text: b, highlighted: false }] }];

  const aParts: { text: string; highlighted: boolean }[] = [];
  const bParts: { text: string; highlighted: boolean }[] = [];

  const commonPrefix = (() => {
    let len = 0;
    while (len < a.length && len < b.length && a[len] === b[len]) len++;
    return len;
  })();

  const commonSuffix = (() => {
    let len = 0;
    while (
      len < a.length - commonPrefix &&
      len < b.length - commonPrefix &&
      a[a.length - 1 - len] === b[b.length - 1 - len]
    ) len++;
    return len;
  })();

  if (commonPrefix > 0) {
    aParts.push({ text: a.slice(0, commonPrefix), highlighted: false });
    bParts.push({ text: b.slice(0, commonPrefix), highlighted: false });
  }

  const aMiddle = a.slice(commonPrefix, a.length - commonSuffix);
  const bMiddle = b.slice(commonPrefix, b.length - commonSuffix);

  if (aMiddle) aParts.push({ text: aMiddle, highlighted: true });
  if (bMiddle) bParts.push({ text: bMiddle, highlighted: true });

  if (commonSuffix > 0) {
    aParts.push({ text: a.slice(a.length - commonSuffix), highlighted: false });
    bParts.push({ text: b.slice(b.length - commonSuffix), highlighted: false });
  }

  return [{ parts: aParts }, { parts: bParts }];
};

const renderValue = (
  value: string | undefined | null,
  diffParts: { text: string; highlighted: boolean }[] | null,
): ReactNode => {
  if (value === '(null)') {
    return <span className={styles.nullValue}>null</span>;
  }
  if (diffParts && diffParts.length > 0) {
    return (
      <span className={styles.diffHighlight}>
        {diffParts.map((part, i) => (
          part.highlighted
            ? <mark key={i} className={styles.charDiff}>{part.text}</mark>
            : <span key={i}>{part.text}</span>
        ))}
      </span>
    );
  }
  return <span>{value ?? ''}</span>;
};

const getRowDiffClass = (differenceType: string): string => {
  switch (differenceType) {
    case 'Modified': return styles.rowModified;
    case 'Added': return styles.rowAdded;
    case 'Removed': return styles.rowRemoved;
    case 'Unmatched': return styles.rowUnmatched;
    case 'Matching': return styles.rowMatching;
    default: return '';
  }
};

interface GroupedSection {
  configName: string;
  mismatches: MismatchDetail[];
  diffItems: DifferenceItem[];
}

const EnvironmentPane = ({
  side,
  folder,
  filteredMismatches,
  filteredDiffItems,
  activeDifferenceKey,
  scrollRef,
  onScroll,
  collapsedSections,
  onToggleSection,
  onShowMore,
  envAName,
  envBName,
}: EnvironmentPaneProps) => {
  const envName = side === 'A' ? envAName : envBName;

  const isSpecialState = !folder ||
    (folder.status === 'OnlyInA' && side === 'B') ||
    (folder.status === 'OnlyInB' && side === 'A') ||
    folder.status === 'Failed';

  const grouped = useMemo<GroupedSection[]>(() => {
    if (isSpecialState) return [];
    const sectionMap = new Map<string, GroupedSection>();

    filteredMismatches.forEach((m) => {
      let section = sectionMap.get(m.configName);
      if (!section) {
        section = { configName: m.configName, mismatches: [], diffItems: [] };
        sectionMap.set(m.configName, section);
      }
      section.mismatches.push(m);
    });

    filteredDiffItems
      .filter(d => d.mismatchCount === 0)
      .forEach((d) => {
        let section = sectionMap.get(d.configName);
        if (!section) {
          section = { configName: d.configName, mismatches: [], diffItems: [] };
          sectionMap.set(d.configName, section);
        }
        section.diffItems.push(d);
      });

    return Array.from(sectionMap.values());
  }, [filteredMismatches, filteredDiffItems, isSpecialState]);

  const charDiffCache = useMemo(() => {
    if (isSpecialState) return new Map<string, { parts: { text: string; highlighted: boolean }[] }[]>();
    const cache = new Map<string, { parts: { text: string; highlighted: boolean }[] }[]>();
    filteredMismatches.forEach((m) => {
      if (m.differenceType === 'Modified' && m.valueInA !== '(null)' && m.valueInB !== '(null)') {
        const key = `${m.configName}-${m.configSubElementName}`;
        cache.set(key, computeCharDiff(m.valueInA ?? '', m.valueInB ?? ''));
      }
    });
    return cache;
  }, [filteredMismatches, isSpecialState]);

  if (!folder) {
    return (
      <div className={styles.pane}>
        <div className={styles.paneHeader}>
          <span className={styles.paneLabel}>{envName}</span>
        </div>
        <div className={styles.paneBody}>
          <div className={styles.paneEmpty}>Select a folder to view differences</div>
        </div>
      </div>
    );
  }

  if (folder.status === 'OnlyInA' && side === 'B') {
    return (
      <div className={styles.pane}>
        <div className={styles.paneHeader}>
          <span className={styles.paneLabel}>{envName}</span>
        </div>
        <div className={styles.paneBody}>
          <div className={styles.paneNotPresent}>Not present in {envName}</div>
        </div>
      </div>
    );
  }

  if (folder.status === 'OnlyInB' && side === 'A') {
    return (
      <div className={styles.pane}>
        <div className={styles.paneHeader}>
          <span className={styles.paneLabel}>{envName}</span>
        </div>
        <div className={styles.paneBody}>
          <div className={styles.paneNotPresent}>Not present in {envName}</div>
        </div>
      </div>
    );
  }

  if (folder.status === 'Failed') {
    return (
      <div className={styles.pane}>
        <div className={styles.paneHeader}>
          <span className={styles.paneLabel}>{envName}</span>
        </div>
        <div className={styles.paneBody}>
          <div className={styles.paneError}>{folder.errorMessage || 'Comparison failed for this folder'}</div>
        </div>
      </div>
    );
  }

  const getDiffParts = (m: MismatchDetail): { text: string; highlighted: boolean }[] | null => {
    if (m.differenceType !== 'Modified') return null;
    const key = `${m.configName}-${m.configSubElementName}`;
    const result = charDiffCache.get(key);
    if (!result) return null;
    return side === 'A' ? result[0].parts : result[1].parts;
  };

  const getDiffKey = (m: MismatchDetail): string =>
    `${m.configName}-${m.configSubElementName}-${m.differenceType}`;

  const getDiffItemKey = (d: DifferenceItem): string =>
    `${d.configName}-entry-${d.differenceType}`;

  const isNotPresentOnThisSide = (m: MismatchDetail): boolean => {
    if (m.differenceType === 'Added' && side === 'A') return true;
    if (m.differenceType === 'Removed' && side === 'B') return true;
    return false;
  };

  const isDiffItemNotPresentOnThisSide = (d: DifferenceItem): boolean => {
    if (d.differenceType === 'Added' && side === 'A') return true;
    if (d.differenceType === 'Removed' && side === 'B') return true;
    return false;
  };

  const getValue = (m: MismatchDetail): string => side === 'A' ? m.valueInA : m.valueInB;

  const truncate = (text: string, max = 200): boolean => text.length > max;

  return (
    <div className={styles.pane}>
      <div className={styles.paneHeader}>
        <span className={styles.paneLabel}>{envName}</span>
        <span className={styles.paneSubtitle}>{side === 'A' ? 'Source' : 'Target'}</span>
      </div>

      <div ref={scrollRef} onScroll={onScroll} className={styles.paneBody}>
        {grouped.length === 0 && (
          <div className={folder.status === 'Matching' ? styles.matchingSummary : styles.paneEmpty}>
            {folder.status === 'Matching' ? (
              folder.nodeSummary?.nodesInA === 0 && folder.nodeSummary?.nodesInB === 0 ? (
                <>
                  <CheckCircleOutlined className={styles.matchingSummaryIcon} />
                  <h4 className={styles.matchingSummaryTitle}>Empty Folder</h4>
                  <p className={styles.matchingSummaryText}>
                    No items configured in either environment.
                  </p>
                </>
              ) : (
                <>
                  <CheckCircleOutlined className={styles.matchingSummaryIcon} />
                  <h4 className={styles.matchingSummaryTitle}>All Items Match</h4>
                  <p className={styles.matchingSummaryText}>
                    No differences were found in this folder.
                  </p>
                </>
              )
            ) : (
              'No differences to display'
            )}
          </div>
        )}

        {grouped.map((section) => {
          const isCollapsed = collapsedSections.has(section.configName);

          return (
            <div key={section.configName} className={styles.configSection}>
              <button
                type="button"
                className={styles.sectionHeader}
                onClick={() => onToggleSection(section.configName)}
              >
                {isCollapsed ? <RightOutlined /> : <DownOutlined />}
                <span className={styles.sectionTitle}>{section.configName}</span>
                <span className={styles.sectionCount}>
                  {section.mismatches.length + section.diffItems.length}
                </span>
              </button>

              {!isCollapsed && (
                <div className={styles.sectionRows}>
                  {section.diffItems.map((d) => {
                    const diffKey = getDiffItemKey(d);
                    const isActive = diffKey === activeDifferenceKey;
                    const notPresent = isDiffItemNotPresentOnThisSide(d);

                    return (
                      <div
                        key={diffKey}
                        data-diff-key={diffKey}
                        className={`${styles.compareRow} ${getRowDiffClass(d.differenceType)} ${isActive ? styles.activeRow : ''}`}
                      >
                        <div className={styles.rowLabel}>
                          <span className={styles.rowLabelText}>{d.configName}</span>
                          <span className={styles.rowDiffBadge}>
                            {getDiffTypeLabel(d.differenceType, envAName, envBName)}
                          </span>
                        </div>
                        <div className={styles.rowValue}>
                          {notPresent ? (
                            <span className={styles.notPresent}>Not present in {envName}</span>
                          ) : (
                            <span className={styles.configEntryPresent}>{d.configName}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {section.mismatches.map((m) => {
                    const diffKey = getDiffKey(m);
                    const isActive = diffKey === activeDifferenceKey;
                    const notPresent = isNotPresentOnThisSide(m);
                    const value = getValue(m);
                    const diffParts = getDiffParts(m);

                    return (
                      <div
                        key={diffKey}
                        data-diff-key={diffKey}
                        className={`${styles.compareRow} ${getRowDiffClass(m.differenceType)} ${isActive ? styles.activeRow : ''}`}
                      >
                        <Tooltip title={m.configSubElementNameRaw} placement="topLeft">
                          <div className={styles.rowLabel}>
                            <span className={styles.rowLabelText}>{m.configSubElementName}</span>
                            <span className={styles.rowDiffBadge}>
                              {getDiffTypeLabel(m.differenceType, envAName, envBName)}
                            </span>
                          </div>
                        </Tooltip>
                        <div className={styles.rowValue}>
                          {notPresent ? (
                            <span className={styles.notPresent}>Not present in {envName}</span>
                          ) : (
                            <>
                              <span className={styles.valueText}>
                                {truncate(value) ? (
                                  <>
                                    {renderValue(value.slice(0, 200), diffParts ? diffParts : null)}…
                                    <button
                                      type="button"
                                      className={styles.showMoreLink}
                                      onClick={() => onShowMore(m)}
                                    >
                                      Show more
                                    </button>
                                  </>
                                ) : (
                                  renderValue(value, diffParts)
                                )}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EnvironmentPane;
